import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TwoSvOverrideStatusProvider } from './context/TwoSvOverrideStatusContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import VerifyEmail from './components/VerifyEmail';
import VenueHirePage from './components/VenueHirePage';
import Dashboard from './components/Dashboard';
import Schedule from './components/Schedule';
import Notifications from './components/Notifications';
import SecurityEvents from './components/SecurityEvents';
import { Profile, Bookings, Payments } from './components/Placeholders';
import BookRoom from './components/BookRoomForm';
import { UserManagementPage, AdminRooms, AdminOrganisations, AdminDevices, AdminMedia, AdminPayments, AdminSettings, AdminConsoles } from './components/admin/AdminComponents';
import AccessControlPanelAdvanced from './components/admin/AccessControlPanelAdvanced';
import {
  AdminNetworkTabbedPage,
  AdminNetworkDeviceDetail,
  AdminNetworkDependencies,
  AdminNetworkWarnings,
  AdminNetworkWarningDetail,
  AdminNetworkCameras,
  AdminNetworkSecurity,
  AdminNetworkAnomalies,
  AdminNetworkAnomalyDetail,
  AdminNetworkHealth,
} from './components/admin/AdminNetwork';
import SecurityCamerasPage from './components/admin/SecurityCamerasPage';
import TwoSvOverridesPage from './components/admin/TwoSvOverridesPage';
import RealmAuthorityGroupsPage from './components/admin/RealmAuthorityGroupsPage';
import { TeacherSchedule, TeacherClass } from './components/teacher/TeacherComponents';
import TeacherTermBookings from './components/teacher/TeacherTermBookings';
import TeacherLedgerPage from './components/teacher/TeacherLedgerPage';
import EventSimpleDashboard from './components/event-simple/EventSimpleDashboard';
import BookingsHub from './components/bookings-admin/BookingsHub';
// M004-F0634 §1 (UI-3): /app/ledgerApprovals is a REDIRECT to the Billing
// tab for one release (the !342 page was never merged; Core's already
// delivered notification deep links must still land).
import LedgerApprovalsRedirect from './components/bookings-admin/LedgerApprovalsRedirect';
// M004-F0714: /app/tx/:key opens the transaction history (a redirect, not a page).
import TxRedirect from './routes/TxRedirect';
import SimpleRoomControls from './components/room-controls/SimpleRoomControls';
import PanopticCockpit from './components/panoptic/PanopticCockpit';
import LandingPage from './components/LandingPage';
import ServiceLinkPage from './components/ServiceLinkPage';
import LedgerLegacyRedirect from './components/LedgerLegacyRedirect';
import LedgerLandingPage from './components/LedgerLandingPage';
import LedgerAccountPage from './components/LedgerAccountPage';
import LedgerBillingSettingsPage from './components/LedgerBillingSettingsPage';
import LedgerStatementPage from './components/LedgerStatementPage';
import BookingKeeperDashboard from './components/BookingKeeperDashboard';
import MusicChoreography from './components/choreography/MusicChoreography';
import ChoreographyAuthGate from './components/choreography/ChoreographyAuthGate';
import ChoreographyAuthCallback from './components/choreography/ChoreographyAuthCallback';
import SharedChoreography from './components/choreography/SharedChoreography';
import ReviewInvitation from './components/choreography/ReviewInvitation';
import ReviewerSheet from './components/choreography/ReviewerSheet';
import LinkViewer from './components/sharing/LinkViewer';
import { TwoSvRecoveryPage, TwoSvUndoDisablePage, ReportCompromisePage } from './components/securityLinks/SecurityLinkPages';
import { registerPushServiceWorker } from './services/pushApi';
import './App.css';

function PushBootstrap() {
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    registerPushServiceWorker().catch((error) => {
      console.error('Failed to register push service worker:', error);
    });
  }, [isAuthenticated]);

  return null;
}

function App() {
  return (
    <AuthProvider>
      <Router>
        {/* M001-F075 / M001-D020 §7.1 — one authoritative 2SV override status for
            the whole app. It sits above the routes so the nav banner (rendered by
            Layout) and the /app/admin/2sv-overrides page read the SAME value and
            cannot display contradictory state. */}
        <TwoSvOverrideStatusProvider>
        <PushBootstrap />
        <div className="App">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/hire" element={<VenueHirePage />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            {/* #222: Core-emailed security links (GET renders a confirm screen only) */}
            <Route path="/account/2sv/recovery" element={<TwoSvRecoveryPage />} />
            <Route path="/account/2sv/undo-disable" element={<TwoSvUndoDisablePage />} />
            <Route path="/auth/report-compromise" element={<ReportCompromisePage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/auth/magic-link" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/app/choreo/auth/studioaeye/callback" element={<ChoreographyAuthCallback />} />
            <Route path="/auth/studioaeye/callback" element={<ChoreographyAuthCallback />} />
            <Route path="/schedule" element={<Schedule />} />
            {/* M004-F311 (public Mycelium/PanOptic OAuth-bounce route) removed
                completely, owner ruling 2026-10-04 (M004-F0656 / T-CONN-0249):
                node setup/login goes through /Service-Link (RADIUS only; the
                central /Service-Login grant was retired 2026-10-04) below,
                never a node-console OAuth redirect bounced through WebUI. */}
            {/* M001-F0213 / T-CONN-0215: device-link approval (auth + SAL-4 enforced by page + API) */}
            <Route path="/Service-Link" element={<ServiceLinkPage />} />
            {/* RETIRED (owner ruling 2026-10-04; Core M001-D021 M001-F0222 retired;
                Core #404): the central browser grant /Service-Login page and its
                route are removed, no shim. Node login is RADIUS only. */}
            {/* M004-F0672 / T-M004-0131 (WebUI #106, UI-3): /Ledger is no longer a
                page. It redirects (query preserved) to the canonical nav page
                /app/ledger; Ledger nodes still link here with ?installationUuid=. */}
            <Route path="/Ledger" element={<LedgerLegacyRedirect />} />

            {/* Protected Student/Parent Routes */}
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/notifications"
              element={
                <ProtectedRoute>
                  <Notifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/account/notifications"
              element={<Navigate to="/app/notifications" replace />}
            />
            <Route
              path="/app/account/security/events"
              element={
                <ProtectedRoute>
                  <SecurityEvents />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/account/security/events/:id"
              element={
                <ProtectedRoute>
                  <SecurityEvents />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account/security/events"
              element={<Navigate to="/app/account/security/events" replace />}
            />
            <Route
              path="/account/security/events/:id"
              element={
                <ProtectedRoute>
                  <SecurityEvents />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/bookingsAdmin"
              element={
                <ProtectedRoute>
                  <Bookings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/bookingsAdmin/:id"
              element={
                <ProtectedRoute>
                  <Bookings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/payments"
              element={
                <ProtectedRoute>
                  <Payments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/ledger"
              element={<ProtectedRoute><LedgerLandingPage /></ProtectedRoute>}
            />
            <Route
              path="/app/ledger/:orgId/bookkeeping"
              element={<ProtectedRoute><BookingKeeperDashboard /></ProtectedRoute>}
            />
            <Route
              path="/app/ledger/:orgId/account"
              element={<ProtectedRoute><LedgerAccountPage own /></ProtectedRoute>}
            />
            <Route
              path="/app/ledger/:orgId/statement"
              element={<ProtectedRoute><LedgerStatementPage /></ProtectedRoute>}
            />
            <Route
              path="/app/ledger/:orgId/members"
              element={<ProtectedRoute><LedgerAccountPage /></ProtectedRoute>}
            />
            <Route
              path="/app/ledger/:orgId/billing-settings"
              element={<ProtectedRoute><LedgerBillingSettingsPage /></ProtectedRoute>}
            />
            <Route
              path="/app/ledger/bookkeeping"
              element={<Navigate to="/app/ledger" replace />}
            />
            <Route
              path="/app/mobile-download"
              element={
                <ProtectedRoute roles={['student', 'teacher', 'admin', 'super_admin']}>
                  <Profile initialTab="mobile" />
                </ProtectedRoute>
              }
            />

            {/* Protected Teacher Routes */}
            <Route
              path="/app/book-room"
              element={
                <ProtectedRoute roles={['teacher', 'admin', 'super_admin']}>
                  <BookRoom />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/teacher/schedule"
              element={
                <ProtectedRoute roles={['teacher', 'admin']}>
                  <TeacherSchedule />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/teacher/class/:id"
              element={
                <ProtectedRoute roles={['teacher', 'admin']}>
                  <TeacherClass />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/teacher/classes"
              element={
                <ProtectedRoute roles={['teacher', 'admin']}>
                  <TeacherSchedule />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/teacher/term-planner"
              element={
                <ProtectedRoute roles={['teacher', 'admin', 'super_admin']}>
                  <TeacherTermBookings />
                </ProtectedRoute>
              }
            />
            {/* WebUI #52 / M015-F0032 / T-CI-WEB-TEACHER-LEDGER-001: teacher Ledger page
                (pending/billed). Nav entry is gated on the ledger application in
                Layout; the page itself renders the ledger-offline state (503
                LEDGER_NODE_OFFLINE) gracefully instead of erroring. */}
            <Route
              path="/app/teacher/ledger"
              element={
                <ProtectedRoute roles={['teacher', 'admin']}>
                  <TeacherLedgerPage />
                </ProtectedRoute>
              }
            />
            <Route path="/app/teacher/term-bookings" element={<Navigate to="/app/teacher/term-planner" replace />} />
            <Route path="/app/term-bookings" element={<Navigate to="/app/admin/rooms" replace />} />
            <Route
              path="/app/choreography"
              element={
                <ProtectedRoute>
                  <ChoreographyAuthGate>
                    <MusicChoreography />
                  </ChoreographyAuthGate>
                </ProtectedRoute>
              }
            />
            <Route path="/choreography/shared/:routineId" element={<SharedChoreography />} />
            {/* WebUI issue #31 / M001-D023 §8.8: the owner's WhatsApp-pasteable
                anonymous view-only link. Deliberately top-level, outside every
                ProtectedRoute/ChoreographyAuthGate wrapper — no auth check, no
                account chrome. Core owns the HTTP document at /link/:key
                (headers, non-disclosure 404, rate limit, OG card); this route
                is what Core hands off to on a resolvable key. See
                LinkViewer.js's own header comment for the decided
                Core/WebUI split (studioaeye-Core#61, in flight). */}
            <Route path="/link/:key" element={<LinkViewer />} />
            <Route path="/choreography/review" element={<ReviewInvitation />} />
            <Route path="/app/choreography/review/:resourceId" element={<ProtectedRoute><ChoreographyAuthGate><ReviewerSheet /></ChoreographyAuthGate></ProtectedRoute>} />

            {/* Protected Admin Routes */}
            <Route
              path="/app/admin/dashboard"
              element={<Navigate to="/app/event-simple" replace />}
            />
            <Route
              path="/app/admin/users"
              element={
                <ProtectedRoute roles={['admin']}>
                  <UserManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/admin/users/:id"
              element={
                <ProtectedRoute roles={['admin']}>
                  <UserManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/admin/rooms"
              element={
                <ProtectedRoute roles={['admin']} anyCapability={['room_devices_read']}>
                  <AdminRooms />
                </ProtectedRoute>
              }
            />
            {/* M004-F0609: full areas/groups/priority/auto-matching editor, gated
                identically to /app/admin/rooms above (same roles list). The Access
                tab on /app/admin/rooms is member-centric only (M004-D013 §10). */}
            <Route
              path="/app/admin/rooms/access/advanced"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AccessControlPanelAdvanced />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/admin/organisations"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminOrganisations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/admin/consoles"
              element={
                <ProtectedRoute roles={['super_admin']}>
                  <AdminConsoles />
                </ProtectedRoute>
              }
            />

            {/* Protected Event Simple Routes - accessible by Admin, Teacher, and Event Simple */}
            <Route
              path="/app/event-simple"
              element={
                <ProtectedRoute roles={['event_simple', 'teacher', 'admin']}>
                  <EventSimpleDashboard />
                </ProtectedRoute>
              }
            />

            {/* M005-F0052 (M005-D005 §4.1): Admin/Manager Bookings hub. A new
                route, deliberately NOT /app/bookingsAdmin (that path is the
                shipped M004-F0612/F0613 edit-my-own-booking page and must not
                be re-pointed) and NOT /app/admin/bookings (retired in
                M004-F0690; that path no longer exists). Any authenticated role may reach the page; everything
                on it — tab set, nav entry — is permission-derived inside
                BookingsHub/Layout (WEB-BKHUB-001/002), and the simple member
                view above is untouched by this feature. */}
            <Route
              path="/app/bookings"
              element={
                <ProtectedRoute>
                  <BookingsHub />
                </ProtectedRoute>
              }
            />

            {/* M004-F0634 §1 (UI-3): the legacy /app/ledgerApprovals
                notification deep link redirects to the Billing tab for one
                release. There is NO second page behind this path — the
                redirect is the whole route. */}
            <Route
              path="/app/tx/:key"
              element={
                <ProtectedRoute>
                  <TxRedirect />
                </ProtectedRoute>
              }
            />

            <Route
              path="/app/ledgerApprovals"
              element={
                <ProtectedRoute>
                  <LedgerApprovalsRedirect />
                </ProtectedRoute>
              }
            />

            {/* Protected Simple Room Controls Route - M010 */}
            <Route
              path="/app/room-controls"
              element={
                <ProtectedRoute roles={['simple_room_controls', 'admin']}>
                  <SimpleRoomControls />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/admin/devices"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminDevices />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/admin/media"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminMedia />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/admin/payments"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminPayments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/admin/settings"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/admin/network"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminNetworkTabbedPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/admin/realm-authority/groups"
              element={
                <ProtectedRoute roles={['admin']}>
                  <RealmAuthorityGroupsPage />
                </ProtectedRoute>
              }
            />
            {/* Owner ruling 2026-09-04 (Mantis #1371): door discovery/onboarding is
                removed — RealmAuthority is a protected wall administered only from
                inside RealmAuthority itself. Old bookmarks/links to the doors page
                redirect to the groups page; they must never 404 and must never
                render door-configuration UI. */}
            <Route
              path="/app/admin/realm-authority/doors"
              element={<Navigate to="/app/admin/realm-authority/groups" replace />}
            />
            <Route
              path="/app/admin/security"
              element={<Navigate to="/app/admin/security/cameras" replace />}
            />
            <Route
              path="/app/admin/security/cameras"
              element={
                <ProtectedRoute roles={['admin']}>
                  <SecurityCamerasPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/admin/security/cameras/:feedId"
              element={
                <ProtectedRoute roles={['admin']}>
                  <SecurityCamerasPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/admin/2sv-overrides"
              element={
                <ProtectedRoute roles={['admin']}>
                  <TwoSvOverridesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/panoptic"
              element={
                <ProtectedRoute roles={['admin']}>
                  <PanopticCockpit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/admin/network/devices"
              element={<Navigate to="/app/admin/network?tab=devices" replace />}
            />
            <Route
              path="/app/admin/network/dev-list-viewer"
              element={<Navigate to="/app/admin/network?tab=devices" replace />}
            />
            <Route
              path="/app/admin/network/dependencies"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminNetworkDependencies />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/admin/network/warnings"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminNetworkWarnings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/admin/network/warnings/:id"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminNetworkWarningDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/admin/network/devices/:id"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminNetworkDeviceDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/admin/network/cameras"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminNetworkCameras />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/admin/network/security"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminNetworkAnomalies />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/admin/network/anomalies"
              element={<Navigate to="/app/admin/network/security" replace />}
            />
            <Route
              path="/app/admin/network/anomalies/:id"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminNetworkAnomalyDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/admin/network/health"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminNetworkHealth />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/admin/network/scans"
              element={<Navigate to="/app/admin/network?tab=scans" replace />}
            />

            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
        </TwoSvOverrideStatusProvider>
      </Router>
    </AuthProvider>
  );
}

export default App;
